import {
  DescriptionOutlined,
  EmailOutlined,
  Link as LinkIcon,
} from '@mui/icons-material';
import { Card, IconButton, Snackbar, Tooltip, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useState } from 'react';
import { FaFacebookF, FaLinkedinIn, FaXTwitter } from 'react-icons/fa6';

interface SocialShareProps {
  title?: string;
  url?: string;
}

export default function SocialShareCard({
  title = 'Check out this article',
  url,
}: SocialShareProps) {
  const [open, setOpen] = useState(false);

  // Get current page URL when no URL is provided
  const shareUrl =
    url || (typeof window !== 'undefined' ? window.location.href : '');

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      '_blank',
      'width=600,height=500'
    );
  };

  const shareX = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      '_blank',
      'width=600,height=500'
    );
  };

  const shareLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      '_blank',
      'width=600,height=500'
    );
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setOpen(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Unable to copy URL', error);
    }
  };

  const shareEmail = () => {
    window.location.href = `mailto:?subject=${encodedTitle}&body=${encodeURIComponent(
      `${title}\n\n${shareUrl}`
    )}`;
  };

  const printPage = () => {
    window.print();
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          width: '100%',
          py: 2,
          px: 2.5,
          borderRadius: '10px',
          backgroundColor: '#fff',
          border: '1px solid #e2e6ed',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          mb: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#777',
            fontSize: '12px',
            fontWeight: 500,
            letterSpacing: '.06em',
            mb: 1.2,
          }}
        >
          SHARE THIS ARTICLE
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          {/* Facebook */}
          <Tooltip title="Share on Facebook">
            <IconButton
              onClick={shareFacebook}
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                backgroundColor: '#f4f5f8',
                color: '#4267B2',
                '&:hover': {
                  backgroundColor: '#e8eaf0',
                },
              }}
            >
              <FaFacebookF size={15} />
            </IconButton>
          </Tooltip>

          {/* X */}
          <Tooltip title="Share on X">
            <IconButton
              onClick={shareX}
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                backgroundColor: '#f4f5f8',
                color: '#000',
                '&:hover': {
                  backgroundColor: '#e8eaf0',
                },
              }}
            >
              <FaXTwitter size={15} />
            </IconButton>
          </Tooltip>

          {/* LinkedIn */}
          <Tooltip title="Share on LinkedIn">
            <IconButton
              onClick={shareLinkedIn}
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                backgroundColor: '#f4f5f8',
                color: '#0A66C2',
                '&:hover': {
                  backgroundColor: '#e8eaf0',
                },
              }}
            >
              <FaLinkedinIn size={15} />
            </IconButton>
          </Tooltip>

          {/* Copy Link */}
          <Tooltip title="Copy link">
            <IconButton
              onClick={copyLink}
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                backgroundColor: '#f4f5f8',
                color: '#777',
                '&:hover': {
                  backgroundColor: '#e8eaf0',
                },
              }}
            >
              <LinkIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Email */}
          <Tooltip title="Share via email">
            <IconButton
              onClick={shareEmail}
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                backgroundColor: '#f4f5f8',
                color: '#777',
                '&:hover': {
                  backgroundColor: '#e8eaf0',
                },
              }}
            >
              <EmailOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Print */}
          <Tooltip title="Print article">
            <IconButton
              onClick={printPage}
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                backgroundColor: '#f4f5f8',
                color: '#777',
                '&:hover': {
                  backgroundColor: '#e8eaf0',
                },
              }}
            >
              <DescriptionOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Card>

      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={() => setOpen(false)}
        message="Link copied to clipboard"
      />
    </>
  );
}
